import {
  Box,
  Button,
  FormLabel,
  Grid, IconButton,
  InputAdornment,
  Paper,
  TableContainer,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import React, {SyntheticEvent, useEffect, useState} from "react";
import {useResources} from "../../../stores/ResourceProvider";
import Person from "../../../model/Person";
import {differenceInYears, startOfToday} from "date-fns";
import Model from "../../../model/Model";
import {
  FutureCalc,
  IAssetDetail,
  IFutureYear,
  IIncomeItemSummary,
  IPersonTimeline,
} from "../../../components/calculator/Calculator";
import {IncomeType} from "../../../model/Income";
import BasicMenuButton from "../../../components/controls/BasicMenuButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import range from "lodash.range";
import {humanizeString, numberToMoneyFormat, numberToPercentFormat} from "../../../stores/StoreUtilities";
import Section from "../../../components/controls/Section";
import fill from "lodash.fill";
import TaxValue, {TaxValueKey} from "../../../model/TaxValue";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreateTaxValueInput, MaritalStatus, UpdateTaxValueInput} from "../../../API";
import {
  AdditionalDeductionRules,
  StateRates,
  TaxEstimate,
  TaxFilingStatus,
  TaxInput
} from "../../../components/tax/TaxEstimator";
import FormValidator from "../../../components/form/FormValidator";
import {parseNumber} from "../../../model/ModelUtilities";
import ExpandIcon from '@mui/icons-material/Expand';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import Plan from "../../../model/Plan";
import ViewMenu from "../../../components/controls/ViewMenu";
import TaxableIncomeEstimateChart from "./charts/TaxableIncomeEstimateChart";
import LifetimeEstimatedTaxesChart from "./charts/LifetimeEstimatedTaxesChart";
import TaxableIncomeByTaxBracketChart from "./charts/TaxableIncomeByTaxBracketChart";
import TaxTables from "./charts/TaxTables";
import Tracking from "../../../components/Tracking";
import {useNavigate} from "react-router-dom";
import RoutesConfig from "../../../RoutesConfig";
import LifetimeTaxTable from "./charts/LifetimeTaxTable";
import IModelItem from "../../../model/IModelItem";
import StateSelector, {stateCodeToNumber, stateNumberToCode} from "../../../components/form/StateSelector";
import InfoIcon from '@mui/icons-material/InfoOutlined';
import {globalColors} from "../../../styles/globalStyles";
import Visible from "../../../components/Visible";
import useIsMobile from "../../../components/useIsMobile";
import BarChartIcon from "@mui/icons-material/BarChart";
import MobileDrawerButton from "../../../components/controls/MobileDrawerButton";
import ProjectionErrorAlert from "../ProjectionErrorAlert";

export enum TaxesChartView {
  LifetimeEstTax,
  TaxableIncomeEstTax,
  TaxableIncomeByTaxBracket,
  TaxTables
}

enum TaxSection {
  Household,
  Income,
  Tax,
  Payments,
  FederalResults,
  StateResults,
  OtherTaxWithholding,
  Totals
}

const ViewStorageKey = "RBC.TaxesViewSettings"
export interface ITaxesViewSettings {
  expandLeftPanel: boolean
  expandedSections: boolean[]
  currentYear: number
  chartView: TaxesChartView
  mobileChart: TaxesChartView | -1
}

const TaxesView = ({
  model,
  plan,
  updatedAt,
  year,
  width,
  onChange,
  toggleDrawer
} : {
  model: Model
  plan: Plan
  updatedAt: string
  year?: number
  width: number
  onChange?(item: IModelItem): void
  toggleDrawer: () => void
}) => {
  const isMobile = useIsMobile()
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentPlan, setCurrentPlan] = useState<Plan | undefined>()
  const [currentUpdate, setCurrentUpdate] = useState<string>("")
  const [startYear, setStartYear] = useState<number>(0)
  const [endYear, setEndYear] = useState<number>(0)
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [expandedSections, setExpandedSections] = useState<boolean[]>(fill(new Array<boolean>(TaxSection.Totals+1), false, 0, TaxSection.Totals))
  const [expandLeftPanel, setExpandLeftPanel] = useState<boolean>(false)
  const [futureCalc, setFutureCalc] = useState<FutureCalc | undefined>()
  const [calcError, setCalcError] = useState<string | undefined>()
  const [futureYear, setFutureYear] = useState<IFutureYear | undefined>()
  const [chartView, setChartView] = useState<TaxesChartView>(TaxesChartView.LifetimeEstTax)
  const [mobileChart, setMobileChart] = useState<TaxesChartView | -1 | undefined>(TaxesChartView.LifetimeEstTax)

  const [persons, setPersons] = useState<Person[]>([])
  const [timelines, setTimelines] = useState<(IPersonTimeline | null)[]>([])
  const [taxValues, setTaxValues] = useState<TaxValue[]>([])
  // const [incomeSummary, setIncomeSummary] = useState<IIncomeSummary | undefined>()
  // const [taxSummary, setTaxSummary] = useState<ITaxSummary | undefined>()
  const [defaultFilingStatus, setDefaultFilingStatus] = useState<TaxFilingStatus>(TaxFilingStatus.Single)
  const [filingStatus, setFilingStatus] = useState<TaxFilingStatus>(TaxFilingStatus.Single)
  const [filingStatusYear, setFilingStatusYear] = useState<number>(0)
  const [numberOfFilersOver65, setNumberOfFilersOver65] = useState<number>(0)
  const [adjustmentsToIncome, setAdjustmentsToIncome] = useState<string>("0")
  const [adjustmentsToIncomeYear, setAdjustmentsToIncomeYear] = useState<number>(0)
  const [itemizedDeductionsIn, setItemizedDeductionsIn] = useState<string>("0")
  const [itemizedDeductionsInYear, setItemizedDeductionsInYear] = useState<number>(0)
  const [autoLoanInterest, setAutoLoanInterest] = useState<string>("0")
  const [autoLoanInterestYear, setAutoLoanInterestYear] = useState<number>(0)
  const [charitableContributions, setCharitableContributions] = useState<string>("0")
  const [charitableContributionsYear, setCharitableContributionsYear] = useState<number>(0)
  const [overtimeIncome, setOvertimeIncome] = useState<string>("0")
  const [overtimeIncomeYear, setOvertimeIncomeYear] = useState<number>(0)
  const [tipIncome, setTipIncome] = useState<string>("0")
  const [tipIncomeYear, setTipIncomeYear] = useState<number>(0)
  const [otherQualifyingDeductionsToTaxableIncome, setOtherQualifyingDeductionsToTaxableIncome] = useState<string>("0")
  const [otherQualifyingDeductionsToTaxableIncomeYear, setOtherQualifyingDeductionsToTaxableIncomeYear] = useState<number>(0)
  const [childTaxCreditOrCreditForOtherDependents, setChildTaxCreditOrCreditForOtherDependents] = useState<string>("0")
  const [childTaxCreditOrCreditForOtherDependentsYear, setChildTaxCreditOrCreditForOtherDependentsYear] = useState<number>(0)
  const [additionalCreditsAndPayments, setAdditionalCreditsAndPayments] = useState<string>("0")
  const [additionalCreditsAndPaymentsYear, setAdditionalCreditsAndPaymentsYear] = useState<number>(0)
  const [earnedIncomeCredit, setEarnedIncomeCredit] = useState<string>("0")
  const [earnedIncomeCreditYear, setEarnedIncomeCreditYear] = useState<number>(0)
  const [refundableAdditionalChildTaxCredits, setRefundableAdditionalChildTaxCredits] = useState<string>("0")
  const [refundableAdditionalChildTaxCreditsYear, setRefundableAdditionalChildTaxCreditsYear] = useState<number>(0)
  const [otherPaymentsAndRefundableCredits, setOtherPaymentsAndRefundableCredits] = useState<string>("0")
  const [otherPaymentsAndRefundableCreditsYear, setOtherPaymentsAndRefundableCreditsYear] = useState<number>(0)
  const [stateTaxState, setStateTaxState] = useState<string>("")
  const [stateTaxStateYear, setStateTaxStateYear] = useState<number>(0)
  const [stateTaxRate, setStateTaxRate] = useState<string>("0")
  const [stateTaxRateYear, setStateTaxRateYear] = useState<number>(0)
  const [taxInput, setTaxInput] = useState<TaxInput>(new TaxInput())
  const [taxEstimate, setTaxEstimate] = useState<TaxEstimate>(new TaxEstimate())
  const [isLocked, setIsLocked] = useState<boolean>(false)

  const theme = useTheme()

  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
    fullLabelCell: {
      width: "85%"
    },
    primaryLabelCell: {
      width: "55%"
    },
    secondaryLabelCell: {
      width: "15%",
      textAlign: "right",
      textWrap: "nowrap",
      border: "1px solid rgba(224, 224, 224, 1)"
    },
    dataCell: {
      width: "15%",
      textAlign: "right",
      border: "1px solid rgba(224, 224, 224, 1)"
    },
    labelItem : {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1),
      border: "1px solid rgba(224, 224, 224, 1)",
      fontSize: 14,
      lineHeight: 1.25
    },
    valueItem : {
      padding: theme.spacing(1),
      textAlign: "right",
      border: "1px solid rgba(224, 224, 224, 1)",
      fontSize: 14,
      backgroundColor: theme.palette.grey["50"]
    },
    detailItem : {
      padding: theme.spacing(1),
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      backgroundColor: "rgb(221, 221, 221)",
      // border: "1px solid rgba(224, 224, 224, 1)",
      fontSize: 14,
    },
    detailLabel : {
      padding: theme.spacing(1),
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      backgroundColor: "rgb(249, 249, 249)",
      // border: "1px solid rgba(224, 224, 224, 1)",
      fontSize: 14,
    },
    detailValue : {
      padding: theme.spacing(1),
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      textAlign: "right",
      // border: "1px solid rgba(224, 224, 224, 1)",
      fontSize: 14,
      backgroundColor: "rgb(249, 249, 249)",
      // backgroundColor: theme.palette.grey["50"]
    },
    fieldItem: {
      padding: 0,
      marginBottom: 0,
      textAlign: "right",
      fontSize: 14,
      ".MuiFormControl-root": {
        marginBottom: 0,
      },
      border: "1px solid rgba(224, 224, 224, 1)",
    },
    autocompleteField: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1)
    },
    outlinedTextField: {
      marginBottom: 0,
      // ".MuiOutlinedInputBase-root": {
      //   borderRadius: 0,
      //   '& fieldset': {
      //     borderRadius: 0,
      //   },
      // },
      // ".MuiInputBase-root": {
      //   borderRadius: 0
      // },
      // '& .MuiOutlinedInput-root': {
      //   '& fieldset': {
      //     borderRadius: 0,
      //   },
      // },
    },
    outlineTextFieldInputProps: {
      fontSize: 16,
      paddingRight: 0,
      textAlign: "right",
      "&::placeholder": {
        textAlign: "right",
      }
    },
    inputAdornment: {
      fontSize: 14,
      ".MuiTypography-body1": {
        fontSize: 14
      }
    }
  }

  const toolTips = {
    autoLoanInterest: `Deduction for interest on new U.S.-made personal vehicles bought after 2024. Up to $10,000 per year. Phases out above $100k (single) / $200k (joint).`,
    charitableDeduction: `From 2026 on, even if you don’t itemize, you can deduct cash gifts to charity—up to $1k single or $2k joint. Cash only, no stocks or goods, and not for donor-advised funds. The $1,000 / $2,000 charitable deduction is only for taxpayers who don’t itemize. If you itemize, your charitable gifts belong on Schedule A.`,
    overtimeIncome: `You can deduct overtime pay (the extra above your normal rate) up to $12.5k single or $25k joint. Phases out once income passes $150k/$300k.`,
    enhancedSeniorDeduction: `Extra deduction for seniors 65+: $6,000 single / $12,000 joint. Reduces by 6% of income over $75k (single) or $150k (joint) until fully phased out at $175k / $250k.`,
    tipIncome: `Deduction for tip income (up to $25,000) from jobs that traditionally receive tips. Phases out above $150k MAGI (single) / $300k (joint).`
}

  const { modelStore, calculator } = useResources()
  const navigate = useNavigate()

  useEffect(() => {
    // Scroll to top
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 200);
  }, [])

  useEffect(() => {
    loadView(model, plan)
    // console.log(`TaxesView updatedAt: ${updatedAt}`)
  }, [model, plan, updatedAt])

  const loadView = async (model: Model, plan: Plan) => {
    if (!currentModel || model.id !== currentModel.id || model.updatedAt !== currentModel.updatedAt ||
        updatedAt !== currentUpdate) {
      console.log(`TaxesView.loadView`)

      setCurrentModel(model)
      setIsLocked(model.isLocked())
      setCurrentPlan(plan)
      setCurrentUpdate(updatedAt)
      setPersons(model.persons)
      const lines = model.persons.map((p: Person) => calculator.personTimeline((p)))
      setTimelines(lines)

      const startDate = startOfToday()
      let endDate = startDate

      let defaultFilingStatus: TaxFilingStatus = TaxFilingStatus.Single
      if ((model.persons.length > 0 && model.persons[0].maritalStatus === MaritalStatus.Married) ||
        (model.persons.length > 1 && model.persons[1].maritalStatus === MaritalStatus.Married)) {
        defaultFilingStatus = TaxFilingStatus.MarriedFilingJointly
      }
      setDefaultFilingStatus(defaultFilingStatus)
      const filingStatus = model.taxValues.find((value: TaxValue) => value.key === "filingStatus")
      if (!filingStatus) {
        const input : CreateTaxValueInput = {
          userId: model.userId,
          accountId: model.accountId,
          modelId: model.id,
          year: (new Date()).getFullYear(),
          key: "filingStatus",
          value: defaultFilingStatus
        }
        const taxValue = await modelStore.createTaxValue(input)
        if (taxValue) {
          console.log(`Default filing status tax value created: ${filingStatus}`)
        }
      }


      if (lines.length >= 0 && lines[0]) {
        endDate = lines[0].lifeExpectancyDate
        if (lines.length >= 1 && lines[1]) {
          if (lines[1]?.lifeExpectancyDate.getTime() > endDate.getTime()) {
            endDate = lines[1]?.lifeExpectancyDate
          }
        }
      }
      setTaxValues(model.taxValues)
      setStartYear(startDate.getFullYear())
      setCurrentYear(startDate.getFullYear())
      setEndYear(endDate.getFullYear())
    }

    // Load localStorage view settings
    loadSettings()
  }

  useEffect(() => {
    if (currentModel && timelines && taxValues) {
      console.log(`TaxesView updateValues`)
      let filingStatus = defaultFilingStatus
      let filingStatusYear = currentYear
      let taxValue = getTaxValueObj("filingStatus")
      if (taxValue && taxValue.value !== null) {
        filingStatus = taxValue.value
        filingStatusYear = taxValue.year
      } else {
        filingStatus = TaxFilingStatus.Single
        filingStatusYear = 0
      }
      if (filingStatus === TaxFilingStatus.MarriedFilingJointly && model.getExpectedMaritalStatus(currentYear) === MaritalStatus.Single) {
        // Override for death of spouse
        filingStatus = TaxFilingStatus.Single
        filingStatusYear = currentYear
      }
      setFilingStatus(filingStatus)
      setFilingStatusYear(filingStatusYear)

      setNumberOfFilersOver65(getNumberOfFilersOver65())

      taxValue = getTaxValueObj("adjustmentsToIncome")
      if (taxValue && taxValue.value !== null) {
        setAdjustmentsToIncome(taxValue.value.toString())
        setAdjustmentsToIncomeYear(taxValue.year)
      } else {
        setAdjustmentsToIncome("0")
        setAdjustmentsToIncomeYear(0)
      }

      taxValue = getTaxValueObj("itemizedDeductionsIn")
      if (taxValue && taxValue.value !== null) {
        setItemizedDeductionsIn(taxValue.value.toString())
        setItemizedDeductionsInYear(taxValue.year)
      } else {
        setItemizedDeductionsIn("0")
        setItemizedDeductionsInYear(0)
      }
      
      // Non-itemized deductions
      taxValue = getTaxValueObj("autoLoanInterest")
      if (taxValue && taxValue.value !== null) {
        setAutoLoanInterest(taxValue.value.toString())
        setAutoLoanInterestYear(taxValue.year)
      } else {
        setAutoLoanInterest("0")
        setAutoLoanInterestYear(0)
      }

      taxValue = getTaxValueObj("charitableContributions")
      if (taxValue && taxValue.value !== null) {
        setCharitableContributions(taxValue.value.toString())
        setCharitableContributionsYear(taxValue.year)
      } else {
        setCharitableContributions("0")
        setCharitableContributionsYear(0)
      }

      taxValue = getTaxValueObj("overtimeIncome")
      if (taxValue && taxValue.value !== null) {
        setOvertimeIncome(taxValue.value.toString())
        setOvertimeIncomeYear(taxValue.year)
      } else {
        setOvertimeIncome("0")
        setOvertimeIncomeYear(0)
      }

      taxValue = getTaxValueObj("tipIncome")
      if (taxValue && taxValue.value !== null) {
        setTipIncome(taxValue.value.toString())
        setTipIncomeYear(taxValue.year)
      } else {
        setTipIncome("0")
        setTipIncomeYear(0)
      }
      
      taxValue = getTaxValueObj("otherQualifyingDeductionsToTaxableIncome")
      if (taxValue && taxValue.value !== null) {
        setOtherQualifyingDeductionsToTaxableIncome(taxValue.value.toString())
        setOtherQualifyingDeductionsToTaxableIncomeYear(taxValue.year)
      } else {
        setOtherQualifyingDeductionsToTaxableIncome("0")
        setOtherQualifyingDeductionsToTaxableIncomeYear(0)
      }

      taxValue = getTaxValueObj("childTaxCreditOrCreditForOtherDependents")
      if (taxValue && taxValue.value !== null) {
        setChildTaxCreditOrCreditForOtherDependents(taxValue.value.toString())
        setChildTaxCreditOrCreditForOtherDependentsYear(taxValue.year)
      } else {
        setChildTaxCreditOrCreditForOtherDependents("0")
        setChildTaxCreditOrCreditForOtherDependentsYear(0)
      }

      taxValue = getTaxValueObj("additionalCreditsAndPayments")
      if (taxValue && taxValue.value !== null) {
        setAdditionalCreditsAndPayments(taxValue.value.toString())
        setAdditionalCreditsAndPaymentsYear(taxValue.year)
      } else {
        setAdditionalCreditsAndPayments("0")
        setAdditionalCreditsAndPaymentsYear(0)
      }

      taxValue = getTaxValueObj("earnedIncomeCredit")
      if (taxValue && taxValue.value !== null) {
        setEarnedIncomeCredit(taxValue.value.toString())
        setEarnedIncomeCreditYear(taxValue.year)
      } else {
        setEarnedIncomeCredit("0")
        setEarnedIncomeCreditYear(0)
      }

      taxValue = getTaxValueObj("refundableAdditionalChildTaxCredits")
      if (taxValue && taxValue.value !== null) {
        setRefundableAdditionalChildTaxCredits(taxValue.value.toString())
        setRefundableAdditionalChildTaxCreditsYear(taxValue.year)
      } else {
        setRefundableAdditionalChildTaxCredits("0")
        setRefundableAdditionalChildTaxCreditsYear(0)
      }

      taxValue = getTaxValueObj("otherPaymentsAndRefundableCredits")
      if (taxValue && taxValue.value !== null) {
        setOtherPaymentsAndRefundableCredits(taxValue.value.toString())
        setOtherPaymentsAndRefundableCreditsYear(taxValue.year)
      } else {
        setOtherPaymentsAndRefundableCredits("0")
        setOtherPaymentsAndRefundableCreditsYear(0)
      }

      taxValue = getTaxValueObj("stateTaxState")
      if (taxValue && taxValue.value !== null) {
        setStateTaxState(stateNumberToCode(taxValue.value))
        setStateTaxStateYear(taxValue.year)
      } else {
        setStateTaxState("")
        setStateTaxStateYear(0)
      }

      taxValue = getTaxValueObj("stateTaxRate")
      if (taxValue && taxValue.value !== null) {
        setStateTaxRate(taxValue.value.toString())
        setStateTaxRateYear(taxValue.year)
      } else {
        setStateTaxRate("0")
        setStateTaxRateYear(0)
      }

      if (currentModel) {
        estimateTax()
      }
    }
  }, [currentYear, currentModel, taxValues])

  useEffect(() => {
    if (currentModel) {
      saveSettings()
    }
  }, [currentYear, expandedSections, expandLeftPanel, chartView, mobileChart])

  const loadSettings = () => {
    const thisYear = (new Date()).getFullYear()
    const data = window.localStorage.getItem(ViewStorageKey)
    if (data) {
      const settings: ITaxesViewSettings = JSON.parse(data)
      if (isMobile) {
        setExpandLeftPanel(false)
      } else {
        setExpandLeftPanel(settings.expandLeftPanel !== undefined ? settings.expandLeftPanel : true)
      }
      setExpandedSections([...settings.expandedSections])
      if (year) {
        setCurrentYear(Math.max(year, thisYear))
      } else if (settings.currentYear) {
        setCurrentYear(Math.max(settings.currentYear, thisYear))
      }
      if (settings.chartView) {
        setChartView(settings.chartView)
      }
      if (settings.mobileChart === undefined) {
        setMobileChart(TaxesChartView.LifetimeEstTax)
      } else if (settings.mobileChart >= 0) {
        setMobileChart(settings.mobileChart)
      } else {
        setMobileChart(-1)
      }
    } else {
      if (year) {
        setCurrentYear(Math.max(year, thisYear))
      }
      setExpandLeftPanel(false)
      setExpandedSections(fill(new Array<boolean>(TaxSection.Totals+1), true, 0, TaxSection.Totals))
    }
  }

  const saveSettings = () => {
    const settings = JSON.stringify({
      expandLeftPanel: expandLeftPanel,
      expandedSections: expandedSections,
      currentYear: currentYear,
      chartView: chartView,
      mobileChart: mobileChart
    })
    window.localStorage.setItem(ViewStorageKey, settings)
  }

  const getExpanded = (index: TaxSection) => {
    if (index <= expandedSections.length + 1) {
      return expandedSections[index]
    } else {
      return false
    }
  }

  const setExpanded = (index: TaxSection, value: boolean) => {
    expandedSections[index] = value
    setExpandedSections([...expandedSections])
  }
  
  const handleChangeYear = (year: number) => {
    setCurrentYear(year)
    navigate(`${RoutesConfig.calc.pathname}/taxes/${year}`)
  }

  // const getTaxValue = (key: TaxValueKey, defaultValue?: number): number => {
  //   let value = 0
  //   let taxValue = getTaxValueObj(key)
  //   if (taxValue === undefined) {
  //     value = defaultValue !== undefined ? defaultValue : 0
  //   } else if (taxValue.value !== null) {
  //     value = taxValue.value
  //   }
  //   return value
  // }

  const getTaxValueObj = (key: TaxValueKey) => {
    // Find the matching TaxValue closest, but less then the currentYear
    let found: TaxValue | undefined
    let index = 0
    while (index < taxValues.length && taxValues[index].year <= currentYear) {
      if (taxValues[index].key === key) {
        found = taxValues[index]
      }
      ++index
    }

    return found
  }

  const getTaxValueIndex = (key: TaxValueKey) => {
    // Find the matching TaxValue closest, but less then the currentYear
    let found = -1
    let index = 0
    while (index < taxValues.length && taxValues[index].year <= currentYear) {
      if (taxValues[index].key === key) {
        found = index
      }
      ++index
    }

    return found
  }

  const setTaxValue = async (key: TaxValueKey, value: string) => {
    let index = getTaxValueIndex(key)
    let taxValue: TaxValue | undefined

    if (index >= 0 && taxValues[index].year === currentYear) {
      // Update it
      taxValue = taxValues[index]
      const update: UpdateTaxValueInput = {
        id: taxValue.id,
        value: parseNumber(value, 0)
      }
      taxValue = await modelStore.updateTaxValue(update)
      if (taxValue) {
        taxValues[index] = taxValue
        setTaxValues([...taxValues])
      }
    } else if (currentModel) {
      const input: CreateTaxValueInput = {
        accountId: currentModel.accountId,
        userId: currentModel.userId,
        modelId: currentModel.id,
        year: currentYear,
        key: key,
        value: parseNumber(value, 0)
      }
      taxValue = await modelStore.createTaxValue(input)
      if (taxValue) {
        const array = [...taxValues, taxValue]
        array.sort((a: TaxValue, b: TaxValue) => a.year - b.year)
        setTaxValues(array)
      }
    }
    if (onChange && taxValue) {
      onChange(taxValue)
    }
    return taxValue
  }

  const getNumberOfFilersOver65 = () => {
    let count = 0
    const taxDate = new Date(currentYear, 12,  31)
    if (timelines.length > 0 && timelines[0]!.birthDate) {
      const taxAge = differenceInYears(taxDate, timelines[0]!.birthDate)
      if (taxAge >= 65) {
        ++count
      }
    }
    if (timelines.length > 1 && timelines[1]!.birthDate) {
      const taxAge = differenceInYears(taxDate, timelines[1]!.birthDate)
      if (taxAge >= 65) {
        ++count
      }
    }
    return count
  }

  const estimateTax = () => {
    if (!currentPlan || !currentModel) {
      return
    }

    setFutureCalc(undefined)
    setFutureYear(undefined)
    setTaxInput(new TaxInput())
    setTaxEstimate(new TaxEstimate())


    let endYear: number = 0
    const latestLifeExpectancyDate = currentModel.getPlanEndDate(currentPlan)
    if (latestLifeExpectancyDate) {
      const latestLifeExpectancyYear = latestLifeExpectancyDate.getFullYear()
      endYear = latestLifeExpectancyYear
    }

    const calc = calculator.calculateFuture(currentModel, currentPlan.name, endYear)
    // A failed projection keeps the tax results cleared above and shows its error instead
    setCalcError(calc.calcError)
    const futureYear = calc.calcError ? undefined : calc.futureYears[currentYear - startYear]
    if (futureYear && futureYear.taxInput && futureYear.taxEstimate) {
      setFutureCalc(calc)
      setFutureYear(futureYear)
      setTaxInput(futureYear.taxInput)
      setTaxEstimate(futureYear.taxEstimate)
    }
  }

  const renderYearSelector = () => {
    if (startYear > 0) {
      return (
        <BasicMenuButton
          icon={<ExpandMoreIcon sx={{color: theme.palette.primary.main}}/>}
          label={
            <Typography variant="h3" color="primary" sx={{padding:0, margin:0}}>
              {currentYear}
            </Typography>
          }
          labelPosition="left"
          options={range(startYear, endYear).map((year: number) => year.toString())}
          value={currentYear - startYear}
          onClickOption={(index: number) => {
            handleChangeYear(startYear + index)
          }}
        />
      )

    } else {
      return null
    }
  }

  const renderEstimatedTax = () => {
    const estimatedTax = taxEstimate.totalEstimatedTax

    const color = (estimatedTax > 0) ? "red" : "green"
    return (
      <Typography variant="h3" color={color}>{numberToMoneyFormat(estimatedTax, 0)}</Typography>
    )
  }

  const renderHouseholdSection = () => {
    const xp = expandLeftPanel

    return (
      <Section title="Household"
               total={`${persons.length <= 2 ? persons.length : 2}`}
               expanded={getExpanded(TaxSection.Household)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.Household, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp ? 7:5} sx={styles.labelItem}>
              Filing Status
            </Grid>
            <Grid item xs={xp ? 5:7} sx={styles.autocompleteField}>
              <TextFieldValidator
                type="text"
                validators={{ required: true }}
                required
                name="filingStatus"
                label=""
                variant="standard"
                size="small"
                // sx={styles.autocompleteField}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {filingStatusYear > 0 ? filingStatusYear : ""}
                  </InputAdornment>,
                }}
                autocompleteOptions={{
                  freeSolo: false,
                  options: Object.values(TaxFilingStatus).filter((v) => !isNaN(Number(v))),
                  getOptionLabel: (option: number) => {
                    // return "Label"
                    return(option >= TaxFilingStatus.Single && option <= TaxFilingStatus.MarriedFilingJointly ? humanizeString(TaxFilingStatus[option]) : "")
                  },
                  value: filingStatus,
                  disabled: isLocked,
                  onChange: (event: any, value: number, reason: any) => {
                    if (value === null) {
                      // TODO: Handle deleting a tax value
                    } else {
                      setFilingStatus(value)
                      setFilingStatusYear(currentYear)
                      setTaxValue('filingStatus', value.toString())
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp ? 10:8} sx={styles.labelItem}>
              Number of filers over 65
            </Grid>
            <Grid item xs={xp ? 2:4} sx={styles.valueItem}>
              {numberOfFilersOver65}
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderIncomeSection = () => {
    const xp = expandLeftPanel
    const taxableIRADistributions: IAssetDetail[] = futureYear ?
      futureYear.assetDetails.filter((ad: IAssetDetail) => ad.liquidAsset.assetTypeDef.isTaxableIRA && (ad.rmd > 0 || ad.distribution !== 0 || ad.conversion < 0 || !!ad.riderIncome)) : []
    const otherIncomeItems: IIncomeItemSummary[] = futureYear ?
      futureYear.budgetCalc.incomeSummary.byItem.filter(
        (item: IIncomeItemSummary) => item.incomeType === IncomeType.OtherIncome
      ) : []

    return (
      <Section title="Income"
               total={numberToMoneyFormat(taxEstimate.taxableIncome, 0)}
               expanded={getExpanded(TaxSection.Income)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.Income, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (1) Wages, salaries, tips, etc.
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.wagesSalariesTips, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (2a) Tax-exempt interest
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.taxExemptInterest, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (2b) Taxable Interest
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.taxableInterest, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (3a) Qualifying Dividends
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.qualifiedDividends, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (3b) Ordinary Dividends
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.ordinaryDividends, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (4a) Total IRA Distributions
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.totalIraDistributions, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (4b) Taxable Amount
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.taxableIraDistributions, 0)}
            </Grid>
          </Grid>
          {taxableIRADistributions.map((ad: IAssetDetail) =>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={7} sx={styles.detailItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.detailLabel}>
                {ad.liquidAsset.description}
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.detailValue}>
                {numberToMoneyFormat(ad.rmd + -ad.distribution + -ad.conversion + (ad.riderIncome ?? 0), 0)}
              </Grid>
            </Grid>
          )}
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (5a) Total Pensions and Annuities
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.totalPensionsAndAnnuities, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (5b) Taxable Amount
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.taxablePensionAndAnnuities, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (6a) Social Security Benefits
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.totalSocialSecurityBenefits, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (6b) Taxable Amount
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.taxableSocialSecurityBenefits, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?5:8} sx={styles.labelItem}>
              (7a) Long Term Capital Gains
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.qualifiedCapitalGains, 0)}
            </Grid>
            <Grid item xs={xp?3:8} sx={styles.labelItem}>
              (7b) Short Term Capital Gains
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.unqualifiedCapitalGains, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (8) Other Income (Sch 1 Line 9)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.income.otherIncome, 0)}
            </Grid>
          </Grid>
          {otherIncomeItems.map((item: IIncomeItemSummary) =>
            <Grid container direction="row" key={item.id}>
              {xp &&
                <Grid item xs={7} sx={styles.detailItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.detailLabel}>
                {item.description}
                {item.excludeFromNIIT &&
                  <Tooltip title={<Typography variant="body2">Excluded from Net Investment Income Tax (NIIT)</Typography>}>
                    <InfoIcon sx={{ml: 0.5, fontSize: '1rem', color: globalColors.primary}}/>
                  </Tooltip>
                }
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.detailValue}>
                {numberToMoneyFormat(item.total, 0)}
              </Grid>
            </Grid>
          )}
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>(9) Total Income</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.totalIncome, 0)}</b>
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (10) Adjustments to Income (Sch 1 line 26)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ isMoney: true }}
                name="adjustmentsToIncome"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={adjustmentsToIncome}
                disabled={isLocked}
                onChange={(event: any) => {
                  setAdjustmentsToIncome(event.target.value)
                  setAdjustmentsToIncomeYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("adjustmentsToIncome", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                                    {adjustmentsToIncomeYear > 0 ? adjustmentsToIncomeYear : ""}
                                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>(11) Adjusted Gross Income (AGI)</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.adjustedGrossIncome, 0)}</b>
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Tax Bracket
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToPercentFormat(taxEstimate.taxBracket, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (12a) Standard Deduction
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.standardDeduction, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (12b) Itemized Deductions
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="itemizedDeductionsIn"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={itemizedDeductionsIn}
                disabled={isLocked}
                onChange={(event: any) => {
                  setItemizedDeductionsIn(event.target.value)
                  setItemizedDeductionsInYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("itemizedDeductionsIn", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {itemizedDeductionsInYear > 0 ? itemizedDeductionsInYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            {xp &&
              <Grid item xs={xp?5:0} sx={styles.labelItem}>
              </Grid>
            }
            <Grid item xs={xp?7:12} sx={styles.labelItem}>
              <b style={{whiteSpace: "nowrap"}}>Additional Non-Itemized Deductions</b>&nbsp;
            </Grid>
          </Grid>

          <Visible cond={currentYear >= AdditionalDeductionRules.enhancedSenior.start &&
                         currentYear <= AdditionalDeductionRules.enhancedSenior.end}>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={xp?5:0} sx={styles.labelItem}>
                </Grid>
              }
              <Grid item xs={xp?5:8} sx={styles.labelItem}>
                Enhanced Senior Deduction for 65+ (2026-2028)
                <Tooltip title={<Typography variant="body2">{toolTips.enhancedSeniorDeduction}</Typography>}>
                  <InfoIcon sx={{ml: 0.5, color: globalColors.primary}}/>
                </Tooltip>
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.valueItem}>
                {numberToMoneyFormat(taxEstimate.additionalDeductions.senior, 0)}
              </Grid>
            </Grid>
          </Visible>

          <Visible cond={taxInput.itemizedDeductionsIn === 0}>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={xp?5:8} sx={styles.labelItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.labelItem}>
                Charitable Contributions (2025-)
                <Tooltip title={<Typography variant="body2">{toolTips.charitableDeduction}</Typography>}>
                  <InfoIcon sx={{ml: 0.5, color: globalColors.primary}}/>
                </Tooltip>
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.fieldItem}>
                <TextFieldValidator
                  type="number"
                  validators={{ minValue: 0 }}
                  name="charitableContributions"
                  variant="outlined"
                  size="small"
                  sx={styles.outlinedTextField}
                  margin="none"
                  fullWidth
                  hiddenLabel
                  value={charitableContributions}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setCharitableContributions(event.target.value)
                    setCharitableContributionsYear(currentYear)
                  }}
                  onBlur={(event: any) => {
                    setTaxValue("charitableContributions", event.target.value)
                  }}
                  inputProps={{
                    sx: styles.outlineTextFieldInputProps,
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                      {charitableContributionsYear > 0 ? charitableContributionsYear : ""}
                    </InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={xp?2:12} sx={styles.valueItem}>
                {numberToMoneyFormat(taxEstimate.additionalDeductions.charitableContributions, 0)}
              </Grid>
            </Grid>
          </Visible>

          <Visible cond={currentYear <= AdditionalDeductionRules.autoLoanInterest.end}>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={xp?5:8} sx={styles.labelItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.labelItem}>
                Auto Loan Interest (2025-2028)
                <Tooltip title={<Typography variant="body2">{toolTips.autoLoanInterest}</Typography>}>
                  <InfoIcon sx={{ml: 0.5, color: globalColors.primary}}/>
                </Tooltip>
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.fieldItem}>
                <TextFieldValidator
                  type="number"
                  validators={{ minValue: 0 }}
                  name="autoLoanInterest"
                  variant="outlined"
                  size="small"
                  sx={styles.outlinedTextField}
                  margin="none"
                  fullWidth
                  hiddenLabel
                  value={autoLoanInterest}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setAutoLoanInterest(event.target.value)
                    setAutoLoanInterestYear(currentYear)
                  }}
                  onBlur={(event: any) => {
                    setTaxValue("autoLoanInterest", event.target.value)
                  }}
                  inputProps={{
                    sx: styles.outlineTextFieldInputProps,
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                      {autoLoanInterestYear > 0 ? autoLoanInterestYear : ""}
                    </InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={xp?2:12} sx={styles.valueItem}>
                {numberToMoneyFormat(taxEstimate.additionalDeductions.autoLoanInterest, 0)}
              </Grid>
            </Grid>
          </Visible>

          <Visible cond={currentYear <= AdditionalDeductionRules.overtimeIncome.end}>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={xp?5:8} sx={styles.labelItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.labelItem}>
                Overtime Income (2025-2028)
                <Tooltip title={<Typography variant="body2">{toolTips.overtimeIncome}</Typography>}>
                  <InfoIcon sx={{ml: 0.5, color: globalColors.primary}}/>
                </Tooltip>
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.fieldItem}>
                <TextFieldValidator
                  type="number"
                  validators={{ minValue: 0 }}
                  name="overtimeIncome"
                  variant="outlined"
                  size="small"
                  sx={styles.outlinedTextField}
                  margin="none"
                  fullWidth
                  hiddenLabel
                  value={overtimeIncome}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setOvertimeIncome(event.target.value)
                    setOvertimeIncomeYear(currentYear)
                  }}
                  onBlur={(event: any) => {
                    setTaxValue("overtimeIncome", event.target.value)
                  }}
                  inputProps={{
                    sx: styles.outlineTextFieldInputProps,
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                      {overtimeIncomeYear > 0 ? overtimeIncomeYear : ""}
                    </InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={xp?2:12} sx={styles.valueItem}>
                {numberToMoneyFormat(taxEstimate.additionalDeductions.overtimeIncome, 0)}
              </Grid>
            </Grid>
          </Visible>

          <Visible cond={currentYear <= AdditionalDeductionRules.tipIncome.end}>
            <Grid container direction="row">
              {xp &&
                <Grid item xs={xp?5:8} sx={styles.labelItem}>
                </Grid>
              }
              <Grid item xs={xp?3:8} sx={styles.labelItem}>
                Tip Income (2025-2028)
                <Tooltip title={<Typography variant="body2">{toolTips.tipIncome}</Typography>}>
                  <InfoIcon sx={{ml: 0.5, color: globalColors.primary}}/>
                </Tooltip>
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.fieldItem}>
                <TextFieldValidator
                  type="number"
                  validators={{ minValue: 0 }}
                  name="tipIncome"
                  variant="outlined"
                  size="small"
                  sx={styles.outlinedTextField}
                  margin="none"
                  fullWidth
                  hiddenLabel
                  value={tipIncome}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setTipIncome(event.target.value)
                    setTipIncomeYear(currentYear)
                  }}
                  onBlur={(event: any) => {
                    setTaxValue("tipIncome", event.target.value)
                  }}
                  inputProps={{
                    sx: styles.outlineTextFieldInputProps,
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                      {tipIncomeYear > 0 ? tipIncomeYear : ""}
                    </InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={xp?2:12} sx={styles.valueItem}>
                {numberToMoneyFormat(taxEstimate.additionalDeductions.tipIncome, 0)}
              </Grid>
            </Grid>
          </Visible>

          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (13) Any Other Qualifying Deductions to Income
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="otherQualifyingDeductionsToTaxableIncome"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={otherQualifyingDeductionsToTaxableIncome}
                disabled={isLocked}
                onChange={(event: any) => {
                  setOtherQualifyingDeductionsToTaxableIncome(event.target.value)
                  setOtherQualifyingDeductionsToTaxableIncomeYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("otherQualifyingDeductionsToTaxableIncome", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {otherQualifyingDeductionsToTaxableIncomeYear > 0 ? otherQualifyingDeductionsToTaxableIncomeYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>(15) Taxable Income</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.taxableIncome, 0)}</b>
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }
  const renderTaxSection = () => {
    const xp = expandLeftPanel

    return (
      <Section title="Tax"
               total={numberToMoneyFormat(taxEstimate.taxLiability, 0)}
               expanded={getExpanded(TaxSection.Tax)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.Tax, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>(16) Tax</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.taxLiability, 0)}</b>
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (17) Additional Tax (Schedule 2, line 3)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.additionalTax, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (19) Nonrefundable Child Tax Credit or Credit for other Dependents (Schedule 8812)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="childTaxCreditOrCreditForOtherDependents"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={childTaxCreditOrCreditForOtherDependents}
                disabled={isLocked}
                onChange={(event: any) => {
                  setChildTaxCreditOrCreditForOtherDependents(event.target.value)
                  setChildTaxCreditOrCreditForOtherDependentsYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("childTaxCreditOrCreditForOtherDependents", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {childTaxCreditOrCreditForOtherDependentsYear > 0 ? childTaxCreditOrCreditForOtherDependentsYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (20) Nonrefundable Credits (Schedule 3, Line 8)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="additionalCreditsAndPayments"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={additionalCreditsAndPayments}
                disabled={isLocked}
                onChange={(event: any) => {
                  setAdditionalCreditsAndPayments(event.target.value)
                  setAdditionalCreditsAndPaymentsYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("additionalCreditsAndPayments", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {additionalCreditsAndPaymentsYear < 0 ? additionalCreditsAndPaymentsYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (23) Other Taxes
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.otherTaxes + taxEstimate.niitTax, 0)}
            </Grid>
          </Grid>
          {taxInput.otherTaxes > 0 &&
            <Grid container direction="row">
              {xp && <Grid item xs={7} sx={styles.detailItem}></Grid>}
              <Grid item xs={xp?3:8} sx={styles.detailLabel}>
                Self-employment Tax
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.detailValue}>
                {numberToMoneyFormat(taxInput.otherTaxes, 0)}
              </Grid>
            </Grid>
          }
          {taxEstimate.niitTax > 0 &&
            <Grid container direction="row">
              {xp && <Grid item xs={7} sx={styles.detailItem}></Grid>}
              <Grid item xs={xp?3:8} sx={styles.detailLabel}>
                Net Investment Income Tax (NIIT)
              </Grid>
              <Grid item xs={xp?2:4} sx={styles.detailValue}>
                {numberToMoneyFormat(taxEstimate.niitTax, 0)}
              </Grid>
            </Grid>
          }
        </Grid>
      </Section>
    )
  }

  const renderPaymentsSection = () => {
    const xp = expandLeftPanel

    return (
      <Section title="Payments"
               total={numberToMoneyFormat(taxEstimate.totalPayments, 0)}
               expanded={getExpanded(TaxSection.Payments)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.Payments, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (25) Federal Income Tax Withheld
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.federalIncomeTaxWithheld, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (27) Earned Income Credit
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="earnedIncomeCredit"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={earnedIncomeCredit}
                disabled={isLocked}
                onChange={(event: any) => {
                  setEarnedIncomeCredit(event.target.value)
                  setEarnedIncomeCreditYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("earnedIncomeCredit", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {earnedIncomeCreditYear > 0 ? earnedIncomeCreditYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (28) Refundable child tax credit or additional child tax credit (Schedule 8812)
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="refundableAdditionalChildTaxCredits"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={refundableAdditionalChildTaxCredits}
                disabled={isLocked}
                onChange={(event: any) => {
                  setRefundableAdditionalChildTaxCredits(event.target.value)
                  setRefundableAdditionalChildTaxCreditsYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("refundableAdditionalChildTaxCredits", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {refundableAdditionalChildTaxCreditsYear ? refundableAdditionalChildTaxCreditsYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (29-31) Other Payments and Refundable Credits
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="otherPaymentsAndRefundableCredits"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={otherPaymentsAndRefundableCredits}
                disabled={isLocked}
                onChange={(event: any) => {
                  setOtherPaymentsAndRefundableCredits(event.target.value)
                  setOtherPaymentsAndRefundableCreditsYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("otherPaymentsAndRefundableCredits", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {otherPaymentsAndRefundableCreditsYear > 0 ? otherPaymentsAndRefundableCreditsYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>(33) Total Payments</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.totalPayments, 0)}</b>
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderFederalResultsSection = () => {
    const total = taxEstimate.amountOverpaid > 0 ? -taxEstimate.amountOverpaid : taxEstimate.amountOwed
    const xp = expandLeftPanel

    return (
      <Section title="Federal Results"
               total={numberToMoneyFormat(total, 0)}
               expanded={getExpanded(TaxSection.FederalResults)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.FederalResults, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              {taxEstimate.amountOverpaid > 0 &&
                <b>(34) Refund - Amount You Overpaid (including tax credits)</b>
              }
              {taxEstimate.amountOverpaid <= 0 &&
                <span>(34) Refund - Amount You Overpaid (including tax credits)</span>
              }
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {taxEstimate.amountOverpaid > 0 &&
                <b>{numberToMoneyFormat(taxEstimate.amountOverpaid, 0)}</b>
              }
              {taxEstimate.amountOverpaid <= 0 &&
                <span>{numberToMoneyFormat(taxEstimate.amountOverpaid, 0)}</span>
              }
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              {taxEstimate.amountOwed > 0 &&
                <b>(37) Amount You Owe</b>
              }
              {taxEstimate.amountOwed <= 0 &&
                <span>(34) Refund</span>
              }
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {taxEstimate.amountOwed > 0 &&
                <b>{numberToMoneyFormat(taxEstimate.amountOwed, 0)}</b>
              }
              {taxEstimate.amountOwed <= 0 &&
                <span>{numberToMoneyFormat(taxEstimate.amountOwed, 0)}</span>
              }
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Effective Federal Tax Rate
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToPercentFormat(taxEstimate.effectiveTaxRate, 2)}
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderStateResultsSection = () => {
    const xp = expandLeftPanel
    const stateTaxDue = taxEstimate.stateTaxDue

    return (
      <Section title="State Results"
               total={numberToMoneyFormat(stateTaxDue, 0)}
               expanded={getExpanded(TaxSection.StateResults)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.StateResults, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              State Income Tax Withheld
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.stateIncomeTaxWithheld, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              State Tax State
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <StateSelector
                label=""
                variant="outlined"
                value={stateTaxState ?? ""}
                isRequired={false}
                // sx={styles.stateField}
                onChange={(event: any) => {
                  const value = event.target.value
                  setStateTaxState(value)
                  setTaxValue("stateTaxState", stateCodeToNumber(value).toString())
                  const stateRate = (StateRates as any)[value] ?? 0
                  setStateTaxRate(stateRate.toString())
                  setTaxValue("stateTaxRate", stateRate.toString())
                }}
              />
            </Grid>

          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              State Tax Rate
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.fieldItem}>
              <TextFieldValidator
                type="number"
                validators={{ minValue: 0 }}
                name="stateTaxRate"
                variant="outlined"
                size="small"
                sx={styles.outlinedTextField}
                margin="none"
                fullWidth
                hiddenLabel
                value={stateTaxRate}
                disabled={isLocked}
                onChange={(event: any) => {
                  setStateTaxRate(event.target.value)
                  setStateTaxRateYear(currentYear)
                }}
                onBlur={(event: any) => {
                  setTaxValue("stateTaxRate", event.target.value)
                }}
                inputProps={{
                  sx: styles.outlineTextFieldInputProps,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={styles.inputAdornment}>
                    {stateTaxRateYear > 0 ? stateTaxRateYear : ""}
                  </InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Estimated State Income Tax
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.stateTaxAmount, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>{stateTaxDue >= 0 ? "Amount You Owe" : "Refund"}</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(Math.abs(stateTaxDue), 0)}</b>
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderOtherTaxWithholdingSection = () => {
    const total = taxInput.socialSecurityTaxWithheld + taxInput.medicareTaxWithheld +
                  taxInput.localIncomeTaxWithheld
    const xp = expandLeftPanel

    return (
      <Section title="Other Tax Withholding"
               total={numberToMoneyFormat(total, 0)}
               expanded={getExpanded(TaxSection.OtherTaxWithholding)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.OtherTaxWithholding, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Social Security Tax Withheld
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.socialSecurityTaxWithheld, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Medicare Security Tax Withheld
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.medicareTaxWithheld, 0)}
            </Grid>
          </Grid>
          {/*<Grid container direction="row">*/}
          {/*  <Grid item xs={xp?10:8} sx={styles.labelItem}>*/}
          {/*    State Income Tax Withheld*/}
          {/*  </Grid>*/}
          {/*  <Grid item xs={xp?2:4} sx={styles.valueItem}>*/}
          {/*    {numberToMoneyFormat(taxInput.stateIncomeTaxWithheld, 0)}*/}
          {/*  </Grid>*/}
          {/*</Grid>*/}
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Local Income Tax Withheld
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxInput.localIncomeTaxWithheld, 0)}
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderTotalsSection = () => {
    const xp = expandLeftPanel

    return (
      <Section title="Totals"
               total={numberToMoneyFormat(taxEstimate.totalEstimatedTax, 0)}
               expanded={getExpanded(TaxSection.Totals)}
               onChange={(event: SyntheticEvent, value: boolean) => setExpanded(TaxSection.Totals, value)}
      >
        <Grid container direction="column" spacing={0}>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              (24) Total Federal Tax
            </Grid>
            <Grid item xs={4} sm={2} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.totalTax, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Total State Tax
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.stateTaxAmount, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Total Other Tax Withholding
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToMoneyFormat(taxEstimate.totalOtherTaxWithholding, 0)}
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              <b>	Total Estimated Tax</b>
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              <b>{numberToMoneyFormat(taxEstimate.totalEstimatedTax, 0)}</b>
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={xp?10:8} sx={styles.labelItem}>
              Effective Total Tax Rate
            </Grid>
            <Grid item xs={xp?2:4} sx={styles.valueItem}>
              {numberToPercentFormat(taxEstimate.effectiveTotalTaxRate, 2)}
            </Grid>
          </Grid>
        </Grid>
      </Section>
    )
  }

  const renderViewMenu = () => {
    const options = ["Lifetime Tax", "Taxable Income", "Taxable Income By Bracket", "Tax Tables"]

    return (
      <ViewMenu
        view={chartView}
        options={options}
        variant="auto"
        onChange={(index: number) => {
          Tracking.event({action: "Taxes View", label: options[index]})
          setChartView(index)
        }}
      />
    )
  }

  const renderTaxForm = () => {
    return (
      <Box sx={{display: "flex", flexDirection: "column", flexGrow: 1, mt: "4px", pr:"2px"}}>
        <Box sx={{display:"flex", mt:0, pl:0, pb:0, mb:"2px", justifyContent:"stretch", alignItems:"center"}}>
          {isMobile && <MobileDrawerButton onClick={toggleDrawer}/>}
          <Typography variant="h2" color="primary">Federal Taxes</Typography>
          {isMobile &&
            <Box sx={{display:"flex", flexGrow:1, justifyContent:"flex-end", alignItems:"center", pr:1}}>
              <IconButton onClick={toggleMobileChart}>
                <BarChartIcon color="primary"/>
              </IconButton>
            </Box>
          }
        </Box>
        <ProjectionErrorAlert message={calcError}/>
        {isMobile && futureCalc && mobileChart === TaxesChartView.LifetimeEstTax &&
          <Box display="flex" justifyContent="center" alignItems="center" height={220} width={width-16} mt={1}>
            <LifetimeEstimatedTaxesChart height={220} width={width-16} year={currentYear} futureCalc={futureCalc}
                                         onChange={(year: number) => setCurrentYear(year)}
            />
          </Box>
        }
        <Box sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "row",
          justifyContent: "stretch",
          paddingLeft: 1.5,
          paddingRight: 1.5,
          maxHeight: 50
        }}>
          <Box sx={{display: "flex", flexGrow: 0, flexDirection: "column"}}>
            <Box sx={{display: "flex", flexGrow: 0, justifyContent: "space-between", maxHeight: 16}}>
              <FormLabel sx={styles.formLabel}>Tax Year</FormLabel>
            </Box>
            <Box sx={{display: "flex", flexGrow: 0}}>
              {renderYearSelector()}
            </Box>
          </Box>
          <Box sx={{display: "flex", flexDirection: "row", flexGrow: 1, justifyContent: "center", paddingTop: 1}}>
            {!isMobile &&
              <Button color="primary" variant="outlined" size="small" sx={{height: 30}}
                      onClick={() => setExpandLeftPanel(!expandLeftPanel)}
              >
                {expandLeftPanel ?
                  <CloseFullscreenIcon style={{transform: "rotate(45deg)"}}/>
                  :
                  <ExpandIcon style={{transform: "rotate(90deg)"}}/>
                }
              </Button>
            }
          </Box>
          <Box sx={{display: "flex", flexGrow: 0, flexDirection: "column", textAlign: "right"}}>
            <FormLabel sx={styles.formLabel}>Estimated Tax</FormLabel>
            {renderEstimatedTax()}
          </Box>
        </Box>

        <FormValidator id="taxForm">
          <TableContainer component={Paper}>
            {renderHouseholdSection()}
            {renderIncomeSection()}
            {renderTaxSection()}
            {renderPaymentsSection()}
            {renderFederalResultsSection()}
            {renderStateResultsSection()}
            {renderOtherTaxWithholdingSection()}
            {renderTotalsSection()}
          </TableContainer>
        </FormValidator>

      </Box>
    )
  }

  const toggleMobileChart = () => {
    setMobileChart(mobileChart !== -1 ? -1 : TaxesChartView.LifetimeEstTax)
  }

  if (expandLeftPanel) {
    return (
      renderTaxForm()
    )
  } else {
    const leftPanelWidth = isMobile ? "100%" : 360
    const rightPanelWidth = isMobile ? 0 : width - 360 - 24

    return (
      <React.Fragment>
      <Box sx={{display:"flex", flexDirection:"row", mt:0, minHeight: "calc(100vh - 180px)"}}>
        <Box sx={{display:"flex", flexGrow:0, width: { xs:"100%", sm:leftPanelWidth},
          minWidth: { xs:"100%", sm:leftPanelWidth}, minHeight:"100%",
          overflowY:"scroll", flexDirection:"column"}}>
          {renderTaxForm()}
        </Box>
        {!isMobile &&
          <Box sx={{
            display: "flex",
            flexGrow: 1,
            flexDirection: "column",
            marginTop: 1,
            width: rightPanelWidth,
            maxWidth: rightPanelWidth,
          }}>
            <Box display="flex" flexDirection="column" alignContent="left" pl={2} height={50}>
              <FormLabel sx={styles.formLabel}>View</FormLabel>
              {renderViewMenu()}
            </Box>
            <Box display="flex" flexDirection="column" pl={2} mb={0} mr={1}>
              {chartView === TaxesChartView.LifetimeEstTax && futureCalc &&
                <React.Fragment>
                  <LifetimeEstimatedTaxesChart year={currentYear} futureCalc={futureCalc}
                                               onChange={(year: number) => setCurrentYear(year)}
                  />
                  <LifetimeTaxTable year={currentYear} futureCalc={futureCalc}
                                    onChange={(year: number) => setCurrentYear(year)}
                  />
                </React.Fragment>
              }
              {chartView === TaxesChartView.TaxableIncomeEstTax && futureCalc &&
                <TaxableIncomeEstimateChart year={currentYear} futureCalc={futureCalc}
                                            onChange={(year: number) => setCurrentYear(year)}
                />
              }
              {chartView === TaxesChartView.TaxableIncomeByTaxBracket && futureCalc &&
                <TaxableIncomeByTaxBracketChart year={currentYear} futureCalc={futureCalc}
                                                onChange={(year: number) => setCurrentYear(year)}
                />
              }
              {chartView === TaxesChartView.TaxTables && taxInput && taxEstimate &&
                <TaxTables year={currentYear} taxInput={taxInput} taxEstimate={taxEstimate}/>
              }
            </Box>
          </Box>
        }
      </Box>
      </React.Fragment>
    )
  }
}

export default TaxesView